import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Trophy } from "lucide-react";

import { Button } from "@/components/ui/button";
import { fetchCollection } from "@/lib/api";
import { Footer } from "@/components/Footer";
import { ChronicleGallery } from "@/components/ChronicleGallery";
import { ShareButtons } from "@/components/ShareButtons";
import { ChroniclePhoto, ClassificationTable, FlickrLink, WinnerStrip } from "@/components/WinnersChronicles";
import { formatDate } from "@/utils/format";

export default function ChroniclePage() {
  const { slug } = useParams();
  const { data: chronicle, isLoading, isError } = useQuery({ queryKey: ["chronicle", slug], queryFn: () => fetchCollection(`/chronicles/by-slug/${slug}`), retry: false });

  useEffect(() => {
    if (chronicle) document.title = `${chronicle.title} · TODOtiro`;
    return () => { document.title = "TODOtiro · Al Plato"; };
  }, [chronicle]);

  return (
    <div className="min-h-screen bg-charcoal text-slate-100" data-testid="chronicle-page">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-5 py-6 lg:px-8">
        <Link to="/" className="font-display text-2xl uppercase tracking-tight text-slate-50" data-testid="chronicle-page-brand">TODO<span className="text-clay">tiro</span> <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">al plato</span></Link>
        <Button asChild variant="outline" className="rounded-none border-white/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.2em] hover:border-clay/60 hover:text-clay">
          <Link to="/#cronicas" data-testid="chronicle-page-back"><ArrowLeft className="h-3 w-3" /> Todas las crónicas</Link>
        </Button>
      </header>
      <main className="mx-auto max-w-5xl px-5 pb-24 lg:px-8">
        {isLoading ? <p className="py-24 text-center text-slate-400" data-testid="chronicle-page-loading">Cargando crónica...</p> : null}
        {isError ? (
          <div className="py-24 text-center" data-testid="chronicle-page-not-found">
            <p className="font-display text-5xl uppercase">Crónica no encontrada</p>
            <Button asChild className="mt-8 rounded-none bg-clay font-mono text-xs uppercase tracking-[0.22em] text-charcoal hover:bg-brass"><Link to="/">Volver a la portada</Link></Button>
          </div>
        ) : null}
        {chronicle ? (
          <article className="poster-card overflow-hidden" data-testid={`chronicle-page-article-${chronicle.id}`}>
            <ChroniclePhoto chronicle={chronicle} className="max-h-[62vh] w-full object-cover image-treatment" hideWhenMissing />
            <div className="p-7 sm:p-10">
              <p className="eyebrow" data-testid="chronicle-page-meta">{chronicle.category || "Crónica"} · {formatDate(chronicle.date)} · {chronicle.location}{chronicle.modality ? ` · ${chronicle.modality}` : ""}{chronicle.author ? ` · Por ${chronicle.author}` : ""}</p>
              <h1 className="mt-4 font-display text-5xl uppercase leading-[0.88] text-slate-50 sm:text-7xl" data-testid="chronicle-page-title">{chronicle.title}</h1>
              {chronicle.winner ? <p className="mt-4 flex items-center gap-2 text-base text-brass" data-testid="chronicle-page-winner"><Trophy className="h-4 w-4" />{chronicle.winner}{chronicle.score != null ? ` · ${chronicle.score} platos` : ""}</p> : null}
              <div className="mt-8"><WinnerStrip chronicle={chronicle} /></div>
              <p className="mt-8 max-w-3xl text-lg leading-8 text-slate-200" data-testid="chronicle-page-excerpt">{chronicle.excerpt}</p>
              <ChronicleGallery chronicle={chronicle} />
              <p className="mt-8 max-w-3xl whitespace-pre-line text-base leading-8 text-slate-300" data-testid="chronicle-page-content">{chronicle.content}</p>
              <div className="mt-10"><ClassificationTable chronicle={chronicle} /></div>
              <div className="mt-10 border border-clay/25 bg-clay/10 p-5">
                <p className="eyebrow">Resumen para prensa y redes</p>
                <p className="mt-3 leading-7 text-slate-200" data-testid="chronicle-page-social">{chronicle.social_summary}</p>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <FlickrLink chronicle={chronicle} />
                <ShareButtons chronicle={chronicle} />
              </div>
            </div>
          </article>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
